// End-to-end encryption utilities using Web Crypto API
// AES-256-GCM for authenticated encryption, PBKDF2 for key derivation

export interface EncryptedBlob {
  version: 1;
  salt: string;      // Base64-encoded 16-byte salt
  iv: string;        // Base64-encoded 12-byte IV
  ciphertext: string; // Base64-encoded encrypted content
}

const PBKDF2_ITERATIONS = 600000;
const SALT_LENGTH = 16;
const IV_LENGTH = 12;

// Fixed app-level salt for auth-derived key derivation.
// The user's Supabase user.id is the PBKDF2 "password" input;
// this salt ensures the derived key is unique to this application.
export const APP_ENCRYPTION_SALT = new Uint8Array([
  0xd3, 0x4a, 0x7b, 0x1f, 0xe8, 0x5c, 0x92, 0x0d,
  0xa6, 0x3e, 0xf1, 0x74, 0xb9, 0x28, 0x6d, 0xc5,
]);

// Convert ArrayBuffer to Base64 string (async, non-blocking for large buffers)
async function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
  const blob = new Blob([buffer]);
  const reader = new FileReader();
  return new Promise((resolve) => {
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.readAsDataURL(blob);
  });
}

// Convert Base64 string to Uint8Array
function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// Generate a random salt for per-encryption use
export function generateSalt(): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(SALT_LENGTH));
}

// Derive an AES-256-GCM key from a password/userId using PBKDF2
export async function deriveKey(password: string, salt: Uint8Array): Promise<CryptoKey> {
  const encoder = new TextEncoder();
  const passwordBuffer = encoder.encode(password);

  // Import password as a key for PBKDF2
  const baseKey = await crypto.subtle.importKey(
    'raw',
    passwordBuffer,
    'PBKDF2',
    false,
    ['deriveBits', 'deriveKey']
  );

  // Derive AES-256-GCM key
  const derivedKey = await crypto.subtle.deriveKey(
    {
      name: 'PBKDF2',
      salt: salt.buffer as ArrayBuffer,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );

  return derivedKey;
}

// Encrypt plaintext content with the derived key
export async function encrypt(plaintext: string, cryptoKey: CryptoKey, salt: Uint8Array): Promise<EncryptedBlob> {
  const encoder = new TextEncoder();
  const plaintextBuffer = encoder.encode(plaintext);

  // Generate random IV for each encryption
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));

  // Encrypt
  const ciphertext = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    cryptoKey,
    plaintextBuffer
  );

  return {
    version: 1,
    salt: await arrayBufferToBase64(salt.buffer as ArrayBuffer),
    iv: await arrayBufferToBase64(iv.buffer as ArrayBuffer),
    ciphertext: await arrayBufferToBase64(ciphertext),
  };
}

// Decrypt an encrypted blob with the derived key
export async function decrypt(encryptedBlob: EncryptedBlob, cryptoKey: CryptoKey): Promise<string> {
  const iv = base64ToArrayBuffer(encryptedBlob.iv);
  const ciphertext = base64ToArrayBuffer(encryptedBlob.ciphertext);

  const decrypted = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv.buffer as ArrayBuffer },
    cryptoKey,
    ciphertext.buffer as ArrayBuffer
  );

  const decoder = new TextDecoder();
  return decoder.decode(decrypted);
}
